import CustomReportChannel from '@/Enums/ReportBuilder/ReportChannel.enum';
import TabPanel from '@/components/TabPanel';
import { FILTER_TAB_ID } from '@/constants/reportBuilder';
import { useRequestSaver } from '@/hooks/RequestSaver';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectIsCurrentWhitelabelLoaded,
  selectVisibleReportBuilderTabs,
} from '@/store/selectors/currentWhitelabel.selectors';
import { resetBeeswaxReportBuilderState } from '@/store/slices/reportBuilder/beeswaxSlice';
import { resetOmniplatformReportBuilderState } from '@/store/slices/reportBuilder/omniplatformSlice';
import { resetYoutubeReportBuilderState } from '@/store/slices/reportBuilder/youtubeSlice';
import { getClients } from '@/store/slices/sharedApi.slice';
import { Typography } from '@mui/material';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import BeeswaxReportBuilder from './BeeswaxReportBuilder';
import ApiTabs from './Components/ApiTabs';
import ReportNoticeModal from './Components/ReportNoticeModal';
import { useModal } from './Hooks/useModal';
import OmniplatformReportBuilder from './OmniplatformReportBuilder';
import YoutubeReportBuilder from './YoutubeReportBuilder';

const ReportBuilder = () => {
  //#region hooks
  const requestSaver = useRequestSaver();
  const dispatch = useAppDispatch();
  const visibleTabs = useAppSelector(selectVisibleReportBuilderTabs);
  const isWhitelabelLoaded = useAppSelector(selectIsCurrentWhitelabelLoaded);
  //#endregion

  //#region state
  const noticeModalState = useModal();
  const [apiTab, setApiTab] = useState<CustomReportChannel>(() => visibleTabs[0] ?? CustomReportChannel.Beeswax);
  //#endregion

  //#region useEffects
  useEffect(() => {
    dispatch(getClients());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      requestSaver.requestSaverAbortAll();
      dispatch(resetBeeswaxReportBuilderState());
      dispatch(resetYoutubeReportBuilderState());
      dispatch(resetOmniplatformReportBuilderState());
    };
  }, []);

  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.includes(apiTab)) {
      setApiTab(visibleTabs[0]);
    }
  }, [apiTab, visibleTabs]);
  //#endregion

  const onApiTabChange = useCallback((_: SyntheticEvent, newValue: CustomReportChannel) => {
    setApiTab(newValue);
  }, []);

  const tabPanelProps = useMemo(
    () => ({
      className: 'w-full',
      baseTabId: FILTER_TAB_ID,
      value: apiTab,
    }),
    [apiTab],
  );

  const tabContentProps = useMemo(
    () => ({
      openNoticeModal: noticeModalState.onOpen,
      requestSaver: requestSaver,
    }),
    [noticeModalState.onOpen, requestSaver],
  );

  return (
    <>
      <div className='w-full p-[30px]'>
        {visibleTabs.length === 0 ? (
          isWhitelabelLoaded && (
            <Typography color='text.secondary'>No report types are enabled for your organization.</Typography>
          )
        ) : (
          <>
            <ApiTabs currentTab={apiTab} visibleTabs={visibleTabs} onTabChange={onApiTabChange} />
            {visibleTabs.includes(CustomReportChannel.Beeswax) && (
              <TabPanel index={CustomReportChannel.Beeswax} {...tabPanelProps}>
                <BeeswaxReportBuilder {...tabContentProps} />
              </TabPanel>
            )}
            {visibleTabs.includes(CustomReportChannel.Youtube) && (
              <TabPanel index={CustomReportChannel.Youtube} {...tabPanelProps}>
                <YoutubeReportBuilder {...tabContentProps} />
              </TabPanel>
            )}
            {visibleTabs.includes(CustomReportChannel.Omniplatform) && (
              <TabPanel index={CustomReportChannel.Omniplatform} {...tabPanelProps}>
                <OmniplatformReportBuilder {...tabContentProps} />
              </TabPanel>
            )}
          </>
        )}
      </div>
      <ReportNoticeModal open={noticeModalState.open} onClose={noticeModalState.onClose} />
    </>
  );
};

export default ReportBuilder;
